/**
 * Module settings that control route accessibility.
 * A route with `settingRequired: ["portfolio"]` is only accessible
 * when the portfolio module is enabled for the school.
 */
export const MODULE_SETTINGS = {
  PORTFOLIO: "portfolio",
  CONVERSATIONS: "conversations",
  STUDENT_MEETING: "studentMeeting",
  TRANSITION_MEETING: "transitionMeeting",
  GROUP_MEETING: "groupMeeting",
  LEARNING_OBJECTIVES: "learningObjectives",
  MANAGE_DATA: "manageData",
  GRADES_ENABLED: "gradesEnabled",
} as const;

export type ModuleSetting =
  (typeof MODULE_SETTINGS)[keyof typeof MODULE_SETTINGS];

/**
 * Menu sections for organizing navigation items.
 */
export const MENU_SECTIONS = {
  MAIN: "main",
  ORGANISE: "organise",
  MANAGE: "manage",
  PROFILE: "profile",
} as const;

export type MenuSection = (typeof MENU_SECTIONS)[keyof typeof MENU_SECTIONS];

/**
 * Available layout components for routes.
 */
export const LAYOUTS = {
  MAIN: "MainLayout",
  MAIN_NO_WRAPPER: "MainLayoutNoWrapper",
  PORTFOLIO: "PortfolioLayout",
  FULL_SCREEN: "FullScreenLayout",
} as const;

export type LayoutName = (typeof LAYOUTS)[keyof typeof LAYOUTS];

/**
 * One-shot query params the notification click flow puts on the target URL
 * to trigger actions there (highlight, open sidebar). The notification store
 * removes them right after execution; anything else that rewrites the query
 * (filter/pagination sync) must never copy them back onto the URL.
 */
export const NOTIFICATION_ACTION_QUERY_KEYS = [
  "actions",
  "data",
  "route",
] as const;

/**
 * Named routes referenced from multiple places.
 *
 * TODO: Once we adopt a more modules-like architecture, these route names
 * could be colocated with their owning module instead of living in this
 * shared constants file.
 */
export const ROUTES = {
  LEARNING_OBJECTIVES_CLUSTER_LIBRARY: "learningObjectivesClusterLibrary",
  LEARNING_OBJECTIVES_LIBRARY: "learningObjectivesLibrary",
  MY_LEARNING_OBJECTIVES: "myLearningObjectives",
  MY_LESSONGROUPS_LEARNING_OBJECTIVES: "myLessongroupsLearningObjectives",
} as const;

export type RouteName = (typeof ROUTES)[keyof typeof ROUTES];
